import type {PayloadAction} from '@reduxjs/toolkit';
import type {FileListItemLayout} from 'interface/stacks/FileListItem';
import type {FolderInfo, FolderContent, FolderDepth} from 'mediafire/types/Folder';
import type {File} from 'mediafire/types/File';

export type Store = {
  layout: FileListItemLayout;
  root: {id: string, name: string};
  items: {[id: string]: FilesItem};
  views: {[id: string]: FilesView};
  sort: FilesSort;
  filter: FilesFilter;
  selection: string[];
  search?: string;
  query?: string;
  drag?: string[];
  pick?: FilesPick;
  previewProgress?: number;
  preview?: {
    id: string,
    name: string,
    jobId?: number,
  };
}

export type Actions = {
  Fail: PayloadAction<{message: string, notify: boolean}>
  Move: PayloadAction<{ids: string[], destination: string}>
  Copy: PayloadAction<{ids: string[], destination: string}>
  Create: PayloadAction<{name: string, id: string}>
  Password: PayloadAction<{id: string, password: string}>
  Search: PayloadAction<{id?: string, query?: string, results?: object}>
  Select: PayloadAction<{ids: string[], noToggle?: boolean, focused?: string, view?: FilesView}>
  Browse: PayloadAction<{id: string}>
  Focus: PayloadAction<{id: string}>
  Drag: PayloadAction<{ids?: string[]}>
  Purge: PayloadAction<{ids: string[]}>
  Archive: PayloadAction<{ids: string[]}>
  Restore: PayloadAction<{ids: string[]}>
  Refresh: PayloadAction<{ids: string[]}>
  Sync: PayloadAction<{ids: string[]}>
  Picking: PayloadAction<FilesPick>
  Sorting: PayloadAction<Store['sort']>
  Filtering: PayloadAction<Store['filter']>
  Layout: PayloadAction<{}>
  Preview: PayloadAction<{id: string, name: string}>
  PreviewJob: PayloadAction<{jobId: number}>
  PreviewProgress: PayloadAction<{progress: number}>
  Load: PayloadAction<{
    id: string,
    root: {
      id: string,
      name: string,
    },
    folders: FolderContent,
    files: FolderContent,
    details?: FolderInfo,
    depth?: FolderDepth,
  }>
  LoadRecent: PayloadAction<{
    files: File[],
  }>
  LoadMulti: PayloadAction<{
    folders?: FolderContent,
    files?: FolderContent,
    items?: FolderContent,
    virtual?: string;
  }>
  LoadTrash: PayloadAction<{
    id: string,
    revision: number,
    folderCount: number,
    fileCount: number,
    folders: any,
    files: any,
  }>
  Chunk: PayloadAction<{
    id: string,
    type: string,
    page: number,
    size: number,
    content?: FolderContent,
  }>
  Update: PayloadAction<{
    id: string,
    name?: string,
    password?: boolean,
    description?: string,
    privacy?: 'public' | 'private',
  }>
}

// HELPERS

export type FilesView = {
  files: string[],
  folders: string[],
}

export type FilesItem = {
  id: string;
  url: string;
  name: string;
  type: string;
  revision: number;
  state: FilesItemState;
  hierarchy: string[][];
  shareLinkEnabled?: boolean;
  private: boolean;
  created: string;
  shared?: string;
  deleted?: string;
  folders?: number;
  files?: number;
  size?: number;
  flag?: number;
  hash?: string;
  owner?: string;
  mimetype?: string;
  downloads?: number;
  filedrop?: boolean;
  password?: boolean;
  description?: string;
  parentKey?: string;
  parentName?: string;
  uploadStatus?: string;
  uploadHashed?: number;
  uploadProgress?: number;
  uploadFailure?: boolean;
}

export type FilesFilter = {
  type: 
      'all'
    | 'folders'
    | 'files'
    | 'images'
    | 'video'
    | 'audio'
    | 'document'
    | 'spreadsheet'
    | 'presentation'
    | 'development'
    | 'public'
    | 'private'
}

export type FilesSort = {
  category:
      'name'
    | 'date'
    | 'size'
    | 'downloads',
  order:
      'asc'
    | 'desc',
}

export type FilesPick = {
  action: 'copy' | 'move' | 'save' | 'restore';
  destination: string;
  ids: string[],
}

export enum FilesItemState {
  isNormal,
  isLoading,
  isMoving,
  isCopying,
  isDeleting,
  isRestoring,
  isPurging,
  isUpdating,
  isHashing,
  isUploading,
  isVerifying,
}

export enum FilesItemFlag {
  isReserved,
  isOwned,
  isViewable,
  isReported,
  isEditable,
  isCopyright,
  isRestricted,
  isWordFiltered,
  isInfected,
}

export enum FilesErrors {
  ApiInternal = 100,
  UserError = 101,
  MissingKey = 102,
  InvalidKey = 103,
  MissingToken = 104,
  InvalidToken = 105,
  InvalidQuickkey = 110,
  MissingQuickkey = 111,
  InvalidFolderkey = 112,
  MissingFolderkey = 113,
  AccessDenied = 114,
  FolderPathConflict = 115,
  InvalidDate = 116,
  MissingFoldername = 117,
  InvalidFilename = 118,
  ZipOwnerNotPro = 149,
  ZipTotalSizeTooBig = 153,
  ZipOwnerInsufficientBandwidth = 155,
  StorageLimitExceeded = 162,
  FolderDepthLimit = 167,
  NotPremiumUser = 173,
  InvalidUrl = 174,
  InvalidUploadKey = 175,
  BandwidthError = 183,
  NotLoggedIn = 188,
  CopyLimitExceeded = 207,
  AsyncJobInProgress = 208,
}
